import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';
import { ProductTeam, ServiceGroup, TeamMember } from './types';

export interface CreateProductTeamRequest {
  name: string;
  /** Catalog user entity ref of the initial team admin, e.g. "user:default/jdoe_arlafoodsamba.com". */
  adminRef: string;
}

export interface ProductTeamsApi {
  listMyTeams(): Promise<ProductTeam[]>;
  listAllTeams(): Promise<ProductTeam[]>;
  getTeam(id: string): Promise<ProductTeam | undefined>;
  createTeam(request: CreateProductTeamRequest): Promise<ProductTeam>;
  /** Caller must be an admin of the team — enforced by the backend. */
  addTeamMember(
    id: string,
    entraId: string,
    role?: 'admin' | 'member',
  ): Promise<ProductTeam>;
  /** Caller must be an admin of the team — enforced by the backend. */
  removeTeamMember(id: string, entraId: string): Promise<ProductTeam>;
  /** Caller must be an admin of the team — enforced by the backend. */
  updateTeamMemberRole(
    id: string,
    entraId: string,
    role: 'admin' | 'member',
  ): Promise<ProductTeam>;
  /** Caller must be an admin of the team — enforced by the backend. */
  renameTeam(id: string, newName: string): Promise<ProductTeam>;
  /** Caller must be an rbac_admin — enforced by the backend. */
  deleteTeam(id: string): Promise<void>;
  /** Caller must be a team member or rbac_admin — enforced by the backend. */
  listServiceGroups(id: string): Promise<ServiceGroup[]>;
}

export const productTeamsApiRef = createApiRef<ProductTeamsApi>({
  id: 'plugin.product-teams.api',
});

// Teams are addressed by id only; members by Entra object id (a GUID). A catalog userRef
// contains a "/" and Azure Front Door decodes an encoded "%2F" back to a literal "/" before it
// reaches the origin, which splits the path segment and 404s — a GUID is path-safe.
const teamPath = (id: string) => `/teams/${encodeURIComponent(id)}`;
const memberPath = (id: string, entraId: string) =>
  `${teamPath(id)}/members/${encodeURIComponent(entraId)}`;

export class ProductTeamsBackendClient implements ProductTeamsApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('product-teams');
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    const url = `${await this.baseUrl()}${path}`;
    const response = await this.fetchApi.fetch(url, {
      ...init,
      headers: {
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
    return response.json() as Promise<T>;
  }

  async listMyTeams(): Promise<ProductTeam[]> {
    return this.request<ProductTeam[]>('/teams/mine');
  }

  async listAllTeams(): Promise<ProductTeam[]> {
    return this.request<ProductTeam[]>('/teams');
  }

  async getTeam(id: string): Promise<ProductTeam | undefined> {
    try {
      return await this.request<ProductTeam>(teamPath(id));
    } catch (e) {
      if (e instanceof ResponseError && e.statusCode === 404) {
        return undefined;
      }
      throw e;
    }
  }

  async createTeam(request: CreateProductTeamRequest): Promise<ProductTeam> {
    return this.request<ProductTeam>('/teams', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async addTeamMember(
    id: string,
    entraId: string,
    role?: 'admin' | 'member',
  ): Promise<ProductTeam> {
    return this.request<ProductTeam>(memberPath(id, entraId), {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
  }

  async removeTeamMember(id: string, entraId: string): Promise<ProductTeam> {
    return this.request<ProductTeam>(memberPath(id, entraId), {
      method: 'DELETE',
    });
  }

  async updateTeamMemberRole(
    id: string,
    entraId: string,
    role: 'admin' | 'member',
  ): Promise<ProductTeam> {
    return this.request<ProductTeam>(memberPath(id, entraId), {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async renameTeam(id: string, newName: string): Promise<ProductTeam> {
    return this.request<ProductTeam>(teamPath(id), {
      method: 'PATCH',
      body: JSON.stringify({ name: newName }),
    });
  }

  async deleteTeam(id: string): Promise<void> {
    const url = `${await this.baseUrl()}${teamPath(id)}`;
    const response = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }
  }

  async listServiceGroups(id: string): Promise<ServiceGroup[]> {
    return this.request<ServiceGroup[]>(`${teamPath(id)}/service-groups`);
  }
}

// Kept for the dev/ playground and tests, now that the app wires up
// ProductTeamsBackendClient by default in plugin.ts. Fixed members/tooling
// content lives only here — real teams get it from the backend, starting
// empty but for the admin.
const toolingGroups: Omit<ServiceGroup, 'productTeamId'>[] = [
  {
    serviceGroupId: '00000000-0000-0000-0000-000000000001',
    kind: 'sonarqube',
    name: 'developerplatform-sonarqube',
    createdBy: 'user:default/mock',
    createdAt: '2026-01-01T00:00:00Z',
  },
  {
    serviceGroupId: '00000000-0000-0000-0000-000000000002',
    kind: 'devbox',
    name: 'Sec-DevBox',
    createdBy: 'user:default/mock',
    createdAt: '2026-01-01T00:00:00Z',
  },
];

let mockTeamSequence = 0;
const nextMockId = () => `mock-${++mockTeamSequence}`;
const cleanName = (name: string) => name.trim().toLowerCase().replace(/ /g, '-');

const devEnablementMembers: TeamMember[] = [
  { name: 'Kenneth Lauge', role: 'admin' },
  { name: 'Mikkel Buchvardt', role: 'admin' },
  { name: 'Sofie Andersen', role: 'member' },
  { name: 'Peter Holm', role: 'member' },
  { name: 'Anne Kjær', role: 'member' },
  { name: 'Camilla Bruun', role: 'member' },
];

const makeTeam = (name: string, rawMembers: TeamMember[]): ProductTeam => {
  const members = rawMembers.map(m => ({
    ...m,
    entraId: m.entraId ?? `mock-entra-${cleanName(m.name)}`,
  }));
  const admins = members.filter(m => m.role === 'admin');
  return {
    id: nextMockId(),
    name,
    nameCleaned: cleanName(name),
    status: 'active',
    createdBy: 'user:default/mock',
    owner: admins[0] ?? members[0],
    admins,
    membersCount: members.length,
    members,
  };
};

const myTeams: ProductTeam[] = [
  makeTeam('Developer Enablement', devEnablementMembers),
  makeTeam('Another product team', [{ name: 'Maja Skovgård nielsen', role: 'admin' }]),
];

const allTeams: ProductTeam[] = [
  ...myTeams,
  makeTeam('Data Platform', [{ name: 'Sofie Andersen', role: 'admin' }]),
  makeTeam('Digital Commerce', [
    { name: 'Peter Holm', role: 'admin' },
    { name: 'Lars Vestergaard', role: 'admin' },
  ]),
  makeTeam('Supply Chain Systems', [{ name: 'Anne Kjær', role: 'admin' }]),
  makeTeam('Consumer Insights', [
    { name: 'Camilla Bruun', role: 'admin' },
    { name: 'Jonas Ravn', role: 'admin' },
  ]),
];

const requireMockTeam = (id: string): ProductTeam => {
  const team = allTeams.find(t => t.id === id);
  if (!team) {
    throw new Error(`Product team "${id}" not found`);
  }
  return team;
};

const requireMockMember = (team: ProductTeam, entraId: string): TeamMember => {
  const member = team.members.find(m => m.entraId === entraId);
  if (!member) {
    throw new Error(`"${entraId}" is not a member of "${team.name}"`);
  }
  return member;
};

export class MockProductTeamsApi implements ProductTeamsApi {
  async listMyTeams(): Promise<ProductTeam[]> {
    return myTeams;
  }

  async listAllTeams(): Promise<ProductTeam[]> {
    return allTeams;
  }

  async getTeam(id: string): Promise<ProductTeam | undefined> {
    return allTeams.find(t => t.id === id);
  }

  async createTeam(request: CreateProductTeamRequest): Promise<ProductTeam> {
    const admin: TeamMember = {
      ref: request.adminRef,
      name: request.adminRef,
      role: 'admin',
    };
    const team = makeTeam(request.name, [admin]);
    allTeams.push(team);
    myTeams.push(team);
    return team;
  }

  async addTeamMember(
    id: string,
    entraId: string,
    role: 'admin' | 'member' = 'member',
  ): Promise<ProductTeam> {
    const team = requireMockTeam(id);
    if (team.members.some(m => m.entraId === entraId)) {
      throw new Error(`"${entraId}" is already a member of "${team.name}"`);
    }
    const member: TeamMember = { ref: `user:default/${entraId}`, name: entraId, entraId, role };
    team.members = [...team.members, member];
    team.admins = team.members.filter(m => m.role === 'admin');
    team.membersCount = team.members.length;
    return team;
  }

  async removeTeamMember(id: string, entraId: string): Promise<ProductTeam> {
    const team = requireMockTeam(id);
    const target = requireMockMember(team, entraId);
    if (
      target.role === 'admin' &&
      team.members.filter(m => m.role === 'admin').length <= 1
    ) {
      throw new Error(`Cannot remove "${target.name}" — "${team.name}" must keep at least one admin`);
    }
    team.members = team.members.filter(m => m !== target);
    team.admins = team.members.filter(m => m.role === 'admin');
    team.membersCount = team.members.length;
    return team;
  }

  async updateTeamMemberRole(
    id: string,
    entraId: string,
    role: 'admin' | 'member',
  ): Promise<ProductTeam> {
    const team = requireMockTeam(id);
    const target = requireMockMember(team, entraId);
    if (
      target.role === 'admin' &&
      role !== 'admin' &&
      team.members.filter(m => m.role === 'admin').length <= 1
    ) {
      throw new Error(`Cannot demote "${target.name}" — "${team.name}" must keep at least one admin`);
    }
    team.members = team.members.map(m => (m === target ? { ...m, role } : m));
    team.admins = team.members.filter(m => m.role === 'admin');
    return team;
  }

  async renameTeam(id: string, newName: string): Promise<ProductTeam> {
    const team = allTeams.find(t => t.id === id);
    if (!team) {
      throw new Error(`Product team "${id}" not found`);
    }
    const trimmedName = newName.trim();
    if (
      trimmedName !== team.name &&
      allTeams.some(t => t.name === trimmedName)
    ) {
      throw new Error(`Product team "${trimmedName}" already exists`);
    }
    team.name = trimmedName;
    team.nameCleaned = cleanName(trimmedName);
    return team;
  }

  async deleteTeam(id: string): Promise<void> {
    const index = allTeams.findIndex(t => t.id === id);
    if (index === -1) {
      throw new Error(`Product team "${id}" not found`);
    }
    allTeams.splice(index, 1);
    const myIndex = myTeams.findIndex(t => t.id === id);
    if (myIndex !== -1) {
      myTeams.splice(myIndex, 1);
    }
  }

  async listServiceGroups(id: string): Promise<ServiceGroup[]> {
    requireMockTeam(id);
    return toolingGroups.map(g => ({ ...g, productTeamId: id }));
  }
}
